import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { Eye, RotateCcw, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import {
  COMBUSTION_KIND_LABELS,
  COMBUSTION_SCENARIOS,
  FIRE_ANSWER_LABELS,
  FIRE_ANSWER_ORDER,
  FIRE_SCENARIOS,
  YEAR_8_RUST_CASES,
  checkCombustionAnswer,
  checkFireAnswer,
  type CombustionKind,
  type CombustionScenario,
  type FireAnswer,
  type FireScenario,
} from './combustionCorrosion';
import { RustInvestigation } from './CombustionCorrosionRust';
import { FeedbackPanel, FireTriangle, FlameModel, Formula } from './CombustionCorrosionVisuals';

type FireTopic = 'triangle' | 'products' | 'rust';

const TOPIC_LABELS: Record<FireTopic, string> = {
  triangle: 'Fire triangle',
  products: 'Combustion products',
  rust: 'Rust conditions',
};

const TOPICS: FireTopic[] = ['triangle', 'products', 'rust'];
const COMBUSTION_KINDS: CombustionKind[] = ['complete', 'incomplete'];

function CasePicker<T extends { id: string; title: string }>({
  label,
  cases,
  selectedId,
  onSelect,
}: {
  label: string;
  cases: T[];
  selectedId: string;
  onSelect: (entry: T) => void;
}) {
  return (
    <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-1" aria-label={label}>
      {cases.map((entry) => (
        <button
          key={entry.id}
          type="button"
          aria-pressed={entry.id === selectedId}
          onClick={() => onSelect(entry)}
          className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold transition-colors motion-reduce:transition-none ${
            entry.id === selectedId
              ? 'border-brand-sky bg-wash-sky text-ink'
              : 'border-hairline bg-surface text-slate2 hover:border-brand-sky/40'
          }`}
        >
          {entry.title}
        </button>
      ))}
    </div>
  );
}

function ChoiceButtons<T extends string>({
  legend,
  options,
  labels,
  value,
  disabled,
  onChange,
}: {
  legend: string;
  options: T[];
  labels: Record<T, string>;
  value: T | null;
  disabled: boolean;
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="mt-5">
      <legend className="text-sm font-extrabold text-ink">{legend}</legend>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            disabled={disabled}
            aria-pressed={value === option}
            onClick={() => onChange(option)}
            className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold disabled:cursor-default ${
              value === option ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'
            }`}
          >
            {labels[option]}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function RevealButtons({
  canReveal,
  onReveal,
  onReset,
  label,
}: {
  canReveal: boolean;
  onReveal: () => void;
  onReset: () => void;
  label: string;
}) {
  return (
    <div className="mt-4 flex flex-wrap gap-3">
      <button
        type="button"
        disabled={!canReveal}
        onClick={onReveal}
        className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Eye className="h-4 w-4" aria-hidden="true" /> {label}
      </button>
      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink hover:bg-surface"
      >
        <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset
      </button>
    </div>
  );
}

function FireTriangleTopic() {
  const [scenario, setScenario] = useState<FireScenario>(FIRE_SCENARIOS[0]);
  const [answer, setAnswer] = useState<FireAnswer | null>(null);
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('FireTriangleTopic', { scenario: tutorSelection(scenario), answer, revealed });
  const result = revealed && answer ? checkFireAnswer(scenario, answer) : null;
  const choose = (next: FireScenario) => {
    setScenario(next);
    setAnswer(null);
    setRevealed(false);
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <aside className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Choose a fire</p>
        <CasePicker label="Fire scenarios" cases={FIRE_SCENARIOS} selectedId={scenario.id} onSelect={choose} />
      </aside>
      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <div className="grid gap-6 md:grid-cols-[16rem_minmax(0,1fr)]">
          <FireTriangle removed={result ? result.expected : null} />
          <div className="min-w-0">
            <h3 className="text-xl font-extrabold text-ink">{scenario.title}</h3>
            <p className="mt-2 font-medium leading-7 text-slate2">{scenario.context}</p>
            <p className="mt-3 text-sm font-medium leading-6 text-slate2">
              A fire needs fuel, heat and oxygen. Take away any one side and the fire goes out.
            </p>
            <ChoiceButtons
              legend="Predict: what does this action do?"
              options={FIRE_ANSWER_ORDER}
              labels={FIRE_ANSWER_LABELS}
              value={answer}
              disabled={revealed}
              onChange={setAnswer}
            />
            <RevealButtons
              canReveal={Boolean(answer) && !revealed}
              onReveal={() => setRevealed(true)}
              onReset={() => choose(scenario)}
              label="Check the triangle"
            />
          </div>
        </div>
        {result ? (
          <FeedbackPanel
            correct={result.correct}
            title={
              result.correct
                ? `Correct — ${FIRE_ANSWER_LABELS[result.expected]}`
                : `Not quite — this action ${FIRE_ANSWER_LABELS[result.expected].toLowerCase()}`
            }
          >
            <p>{result.feedback}</p>
            {result.correct ? null : <p>{scenario.explanation}</p>}
            {scenario.saferAction ? (
              <p className="flex items-start gap-2 rounded-2xl bg-white/75 p-3 text-sm font-bold text-ink">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-brand-coral" aria-hidden="true" />
                <span>Safer action: {scenario.saferAction}</span>
              </p>
            ) : null}
          </FeedbackPanel>
        ) : null}
      </section>
    </div>
  );
}

function CombustionProductsTopic() {
  const [scenario, setScenario] = useState<CombustionScenario>(COMBUSTION_SCENARIOS[0]);
  const [answer, setAnswer] = useState<CombustionKind | null>(null);
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('CombustionProductsTopic', { scenario: tutorSelection(scenario), answer, revealed });
  const result = revealed && answer ? checkCombustionAnswer(scenario, answer) : null;
  const choose = (next: CombustionScenario) => {
    setScenario(next);
    setAnswer(null);
    setRevealed(false);
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <aside className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Choose a flame</p>
        <CasePicker label="Combustion cases" cases={COMBUSTION_SCENARIOS} selectedId={scenario.id} onSelect={choose} />
      </aside>
      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <h3 className="text-xl font-extrabold text-ink">{scenario.title}</h3>
        <p className="mt-2 font-medium leading-7 text-slate2">{scenario.context}</p>
        <p className="mt-2 text-sm font-bold text-ink-soft">Fuel: {scenario.fuel}</p>
        <div className="mt-4 rounded-2xl border border-hairline bg-surface p-4">
          <FlameModel flame={scenario.flame} airSupply={scenario.airSupply} />
        </div>
        <ChoiceButtons
          legend="Predict: is this complete or incomplete combustion?"
          options={COMBUSTION_KINDS}
          labels={COMBUSTION_KIND_LABELS}
          value={answer}
          disabled={revealed}
          onChange={setAnswer}
        />
        <RevealButtons
          canReveal={Boolean(answer) && !revealed}
          onReveal={() => setRevealed(true)}
          onReset={() => choose(scenario)}
          label="Show the products"
        />
        {result ? (
          <FeedbackPanel
            correct={result.correct}
            title={
              result.correct
                ? `Correct — ${COMBUSTION_KIND_LABELS[result.expected]}`
                : `Not quite — this is ${COMBUSTION_KIND_LABELS[result.expected].toLowerCase()}`
            }
          >
            <p>{result.feedback}</p>
            <p className="text-ink">
              <strong>Products in the model:</strong>{' '}
              {scenario.products.map((formula, index) => (
                <span key={formula}>
                  {index > 0 ? ', ' : ''}
                  <Formula formula={formula} />
                </span>
              ))}
            </p>
            <ul className="grid gap-2 sm:grid-cols-2">
              {scenario.evidence.map((item) => (
                <li key={item} className="rounded-2xl bg-white/75 px-4 py-2 text-sm font-bold text-ink-soft">
                  {item}
                </li>
              ))}
            </ul>
          </FeedbackPanel>
        ) : null}
      </section>
    </div>
  );
}

export function FireAndRustMode() {
  const [topic, setTopic] = useState<FireTopic>('triangle');
  useScienceTutorSection('FireAndRustMode', { topic });
  return (
    <div>
      <div className="mb-5 flex flex-wrap gap-2" aria-label="Year 8 topics">
        {TOPICS.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={topic === option}
            onClick={() => setTopic(option)}
            className={`rounded-full border px-4 py-2 text-sm font-extrabold ${
              topic === option ? 'border-ink bg-ink text-white' : 'border-hairline bg-canvas-pure text-slate2 hover:text-ink'
            }`}
          >
            {TOPIC_LABELS[option]}
          </button>
        ))}
      </div>
      {topic === 'triangle' ? <FireTriangleTopic /> : null}
      {topic === 'products' ? <CombustionProductsTopic /> : null}
      {topic === 'rust' ? <RustInvestigation cases={YEAR_8_RUST_CASES} showConditionsBeforeReveal /> : null}
    </div>
  );
}
